/**
 * 服务器监控页的展示件 — 级别 / 类别 chip、近 1h 柱、健康卡、告警条目、日志详情抽屉。
 *
 * 转写自原型 server-monitor.jsx（LevelChip / LvChip / CatChip / Spark / HealthCard / AlertItem / LogDrawer），
 * 只用 tokens 原子类与 ~/ui 组件，数据来自 server-monitor-adapter 的页面行。
 */

import { createElement } from 'react';
import type { AlertRow, HealthCardRow, LogRow } from '~/data/adapters/server-monitor-adapter';
import { Icons } from '~/lib/icons';
import { Drawer } from '~/ui/Dialog';
import { Row } from '~/ui/Pair';
import { Section } from '~/ui/Section';
import { toast } from '~/ui/Toast';

const LEVEL_CHIP: Record<string, string> = { P0: 'chip-neg', P1: 'chip-warn', P2: 'chip-info' };

export function LevelChip({ level }: { level: string }) {
  return (
    <span
      className={`chip ${LEVEL_CHIP[level] || ''}`}
      style={{ fontSize: 10, fontFamily: 'var(--font-mono)' }}
    >
      {level}
    </span>
  );
}

export function LvChip({ lv }: { lv: string }) {
  return (
    <span
      className={`chip ${lv === 'error' ? 'chip-neg' : 'chip-warn'}`}
      style={{ fontSize: 9.5, padding: '0 5px', fontFamily: 'var(--font-mono)' }}
    >
      {lv}
    </span>
  );
}

export function CatChip({ label }: { label: string }) {
  return (
    <span className="chip" style={{ fontSize: 9.5, padding: '0 5px' }}>
      {label}
    </span>
  );
}

function Spark({ data, tone }: { data: number[]; tone: HealthCardRow['state'] }) {
  const max = Math.max(1, ...data);
  const color =
    tone === 'firing' ? 'var(--neg)' : tone === 'warn' ? 'var(--warn)' : 'var(--fg-faint)';
  return (
    <div
      className="hstack"
      style={{ gap: 2, alignItems: 'flex-end', height: 22 }}
      aria-label="近 1 小时每 5 分钟错误数"
    >
      {data.map((v, i) => (
        <div
          key={i}
          style={{
            width: 6,
            height: Math.max(2, (v / max) * 22),
            borderRadius: 1.5,
            background: v ? color : 'var(--border)',
          }}
        />
      ))}
    </div>
  );
}

export function HealthCard({ h, onPick }: { h: HealthCardRow; onPick: () => void }) {
  const tone = h.state;
  const dot = tone === 'firing' ? 'var(--neg)' : tone === 'warn' ? 'var(--warn)' : 'var(--pos)';
  return (
    <div
      className="card"
      role="button"
      tabIndex={0}
      aria-label={`${h.label}健康卡：${h.stateLabel}，点击按该类筛选错误日志`}
      onClick={onPick}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onPick();
        }
      }}
      style={{
        padding: '12px 14px',
        cursor: 'pointer',
        minWidth: 0,
        borderColor: tone === 'firing' ? 'var(--neg)' : undefined,
        background: tone === 'firing' ? 'var(--neg-soft)' : undefined,
      }}
    >
      <div className="hstack" style={{ justifyContent: 'space-between' }}>
        <div className="hstack" style={{ gap: 6 }}>
          {createElement(Icons[h.icon], { size: 13, style: { color: 'var(--fg-muted)' } })}
          <span style={{ fontSize: 12.5, fontWeight: 500 }}>{h.label}</span>
        </div>
        <span className="hstack" style={{ gap: 5, fontSize: 11, color: dot }}>
          <span
            className={tone === 'firing' ? 'pulse-dot' : ''}
            style={{ width: 6, height: 6, borderRadius: 999, background: dot }}
          />
          {h.stateLabel}
        </span>
      </div>
      <div
        className="hstack"
        style={{ justifyContent: 'space-between', alignItems: 'flex-end', marginTop: 10 }}
      >
        <div>
          <div
            className="mono"
            style={{
              fontSize: 20,
              fontWeight: 600,
              color: h.lastHourCount ? 'var(--fg-strong)' : 'var(--fg-faint)',
            }}
          >
            {h.lastHourCount}
          </div>
          <div style={{ fontSize: 10.5, color: 'var(--fg-faint)', marginTop: 1 }}>
            近 1h 错误 · 24h {h.last24hCount} · 最近 {h.lastLabel}
          </div>
        </div>
        <Spark data={h.buckets} tone={tone} />
      </div>
    </div>
  );
}

/** onLogs 缺省（支付时效告警：没有对应的错误日志可下钻）时不显示「日志」按钮 */
export function AlertItem({ a, onLogs }: { a: AlertRow; onLogs?: () => void }) {
  const bg = a.recovered
    ? 'var(--bg-subtle)'
    : a.severity === 'P0'
      ? 'var(--neg-soft)'
      : 'var(--warn-soft)';
  return (
    <div
      className="hstack"
      data-testid="server-monitor-alert"
      style={{
        gap: 10,
        alignItems: 'flex-start',
        padding: '8px 10px',
        border: '1px solid var(--border)',
        borderRadius: 6,
        background: bg,
        opacity: a.recovered ? 0.8 : 1,
      }}
    >
      <LevelChip level={a.severity} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div className="hstack" style={{ gap: 6, flexWrap: 'wrap' }}>
          <CatChip label={a.categoryLabel} />
          <span className="mono" style={{ fontSize: 12, fontWeight: 500 }}>
            {a.route}
          </span>
        </div>
        <div className="mono" style={{ fontSize: 10.5, color: 'var(--fg-muted)', marginTop: 2 }}>
          {a.meta}
        </div>
        <div className="hstack" style={{ gap: 8, marginTop: 4, flexWrap: 'wrap' }}>
          <span className={`chip chip-${a.statusTone}`} style={{ fontSize: 10 }}>
            {a.statusLabel}
          </span>
          <span className="mono" style={{ fontSize: 10.5, color: 'var(--fg-strong)' }}>
            {a.countLabel}
          </span>
        </div>
        <div
          className="mono"
          style={{
            fontSize: 10,
            color: 'var(--fg-faint)',
            marginTop: 3,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}
        >
          <span>
            首次 · {a.firstLabel}　最近 · {a.lastLabel}
          </span>
          <span>{a.noticeLabel}</span>
        </div>
      </div>
      {onLogs && (
        <button
          type="button"
          className="btn btn-sm"
          style={{ flexShrink: 0 }}
          onClick={onLogs}
          title="按该指纹筛选下方错误日志"
        >
          <Icons.IconList size={11} />
          日志
        </button>
      )}
    </div>
  );
}

const PRE_STYLE = {
  margin: 0,
  padding: 10,
  fontSize: 11,
  lineHeight: 1.5,
  background: 'var(--bg-subtle)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  whiteSpace: 'pre-wrap',
  color: 'var(--fg-muted)',
} as const;

export function LogDrawer({
  row,
  command,
  dashboardUrl,
  onClose,
}: {
  row: LogRow;
  command: string;
  dashboardUrl: string | null;
  onClose: () => void;
}) {
  return (
    <Drawer
      title={row.event}
      subtitle={`${row.timeLabel} · ${row.route}`}
      icon="IconList"
      width={620}
      onClose={onClose}
      footer={
        <>
          <button
            type="button"
            className="btn"
            onClick={() => {
              void navigator.clipboard?.writeText(command);
              toast('已复制排查命令', { tone: 'pos', icon: 'IconCopy' });
            }}
          >
            <Icons.IconCopy size={12} />
            复制排查命令
          </button>
          {dashboardUrl ? (
            <a
              className="btn"
              href={dashboardUrl}
              target="_blank"
              rel="noopener noreferrer"
              // 原型此处是 button：去掉链接下划线，与相邻按钮外观一致
              style={{ textDecoration: 'none' }}
              title="打开 Cloudflare Workers Logs（在 Query 框按 requestId 过滤）"
            >
              <Icons.IconExternal size={12} />在 Cloudflare 查看
            </a>
          ) : (
            <button type="button" className="btn" disabled title="未配置 Cloudflare account">
              <Icons.IconExternal size={12} />在 Cloudflare 查看
            </button>
          )}
          <div style={{ flex: 1 }} />
          <button type="button" className="btn btn-primary" onClick={onClose}>
            关闭
          </button>
        </>
      }
    >
      <div style={{ padding: '8px 18px 16px' }}>
        <Section title="请求">
          <Row k="级别" v={<LvChip lv={row.level} />} />
          <Row k="类别" v={<CatChip label={row.categoryLabel} />} />
          <Row k="errorCode" v={row.errorCode} mono />
          <Row k="HTTP" v={row.httpStatus ?? '—'} mono />
          <Row k="路由" v={row.route} mono />
          <Row k="requestId" v={row.requestId ?? '—'} mono last />
        </Section>
        <Section title={row.causeTruncated ? 'cause（超过 4KB 已截断）' : 'cause'}>
          <pre className="mono" style={PRE_STYLE}>
            {row.cause ?? '无 cause 记录'}
          </pre>
        </Section>
        <Section title="本地排查命令">
          <div
            className="mono"
            style={{
              fontSize: 11,
              padding: 10,
              background: 'var(--bg-subtle)',
              border: '1px solid var(--border)',
              borderRadius: 6,
              wordBreak: 'break-all',
            }}
          >
            {command}
          </div>
        </Section>
      </div>
    </Drawer>
  );
}
